"use client"

import { useState, useEffect } from "react"
import MetaTags from "../components/SEO/MetaTags"
import { pageConfigs } from "../utils/seo"

// Team members data
const teamMembers = [
  {
    id: "christina",
    name: "Christina Rama, PT, DPT",
    role: "Co-Founder & Chief Medical Officer",
    image: "/assets/images/teams/christina.png",
    shortDesc: "Physical Therapy expert across multiple states",
    link: "/team/christina",
  },
  {
    id: "vignesh",
    name: "Vignesh Rama",
    role: "Co-Founder & CTO",
    image: "/assets/images/teams/vignesh.png",
    shortDesc: "Two decades of enterprise tech experience",
    link: "/team/vignesh",
  },
  {
    id: "sowmya",
    name: "Sowmya Shetty",
    role: "Co-Founder & Operations",
    image: "/assets/images/teams/sowmya.png",
    shortDesc: "Healthcare entrepreneur and team orchestrator",
    link: "/team/sowmya",
  },
  {
    id: "rachel",
    name: "Rachel Stewart",
    role: "UX Designer",
    image: "/assets/images/teams/rachel.png",
    shortDesc: "Doctor of Physical Therapy turned UX Designer",
    link: "/team/rachel",
  },
  {
    id: "piyush",
    name: "Piyush Patil",
    role: "Founding Engineer",
    image: "/assets/images/teams/Piyush.png",
    shortDesc: "Full-stack engineer with microservices expertise",
    link: "/team/piyush",
  },
  {
    id: "rohan",
    name: "Rohan Nalla",
    role: "AI Engineer",
    image: "/assets/images/teams/Rohan.png",
    shortDesc: "Data Science expert building AI automation",
    link: "/team/rohan",
  },
  // {
  //   id: "yashaswi",
  //   name: "Yashaswi Galhotra",
  //   role: "ML Engineer",
  //   image: "/assets/images/teams/Yashaswi.png",
  //   shortDesc: "AI-driven healthcare solutions specialist",
  //   link: "/team/yashaswi",
  // },
  // {
  //   id: "swagata",
  //   name: "Swagatha Mandal",
  //   role: "Technology Strategist",
  //   image: "/assets/images/teams/Swagatha.png",
  //   shortDesc: "Digital transformation and tech roadmap expert",
  //   link: "/team/swagata",
  // },
]

export default function AboutUs() {
  const [scrollY, setScrollY] = useState(0)
  const [hoveredMember, setHoveredMember] = useState<string | null>(null)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleMemberClick = (memberId: string) => {
    window.location.href = `/team/${memberId}`
  }

  return (
    <>
      <MetaTags
        title={pageConfigs.about.title}
        description={pageConfigs.about.description}
        keywords={pageConfigs.about.keywords}
        canonical={pageConfigs.about.canonical}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#1e3d3b]/20 to-[#1e3d3b]/40"></div>
        <div
          className="relative px-6 py-24 md:py-32 lg:py-40"
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
          }}
        >
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight font-productSans">
              Meet Our Team of{" "}
              <span className="bg-white text-[#1e3d3b] px-4 py-2 rounded-lg inline-block mt-2">
                Innovators
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
              Creators, Designers, and Problem Solvers transforming healthcare through technology
            </p>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
      </section>

      {/* Team Grid Section */}
      <section className="py-20 px-6">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 font-productSans">
              Our Leadership Team
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A diverse group of experts united by our mission to revolutionize healthcare delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={member.id}
                className={`group cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 bg-white/80 backdrop-blur-sm rounded-lg ${hoveredMember === member.id ? "ring-2 ring-[#619DD2]/40" : ""
                  }`}
                style={{
                  animationDelay: `${index * 100}ms`,
                }}
                onMouseEnter={() => setHoveredMember(member.id)}
                onMouseLeave={() => setHoveredMember(null)}
                onClick={() => handleMemberClick(member.id)}
              >
                <div className="p-6">
                  <div className="relative mb-6">
                    <div className="w-32 h-32 mx-auto rounded-full overflow-hidden ring-4 ring-[#619DD2]/20 group-hover:ring-[#619DD2]/40 transition-all duration-300">
                      <img
                        src={member.image || "/placeholder.svg"}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  </div>

                  <div className="text-center space-y-3">
                    <h3 className="font-bold text-lg text-slate-800 group-hover:text-[#619DD2] transition-colors">
                      {member.name}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {member.shortDesc}
                    </p>
                  </div>

                  <button
                    className="w-full mt-4 py-2 px-4 rounded text-slate-600 hover:bg-[#619DD2]/10 hover:text-[#619DD2] transition-colors"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      handleMemberClick(member.id)
                    }}
                  >
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </div>
    </>
  )
}
